import React from "react";

import { Box, Typography } from "@mui/material";
import { AsyncContentMui } from "components/Async-content/AsyncContentMui";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { IPortfolioConstructionPieChart } from "store/types";

interface PieChartProps {
  data: IPortfolioConstructionPieChart;
  loading: boolean;
}

const COLORS = ["#071F35", "#26A6AE", "#C09560", "#92A7BC"];

const titleStyle = {
  fontWeight: "500",
  fontSize: "18px",
  fontFamily: "Roboto",
  color: "#071F35"
};

export const PieChart: React.FC<PieChartProps> = ({ data, loading }) => {
  const points = data?.data?.data ?? [];
  const hasData = points.length > 0;
  const total = points.reduce((sum, p) => sum + (p.y ?? 0), 0);

  // Pie only — the legend is rendered as a separate HTML list (right) so it can
  // never overlap the chart, regardless of container width.
  const pieOptions: Highcharts.Options = {
    chart: { type: "pie", height: 205, marginTop: 36, marginBottom: 8, backgroundColor: "transparent" },
    title: { text: data?.title || "Portfolio Allocation", align: "left", x: 4, style: titleStyle },
    tooltip: { headerFormat: "", pointFormat: "{point.name}: <b>${point.y:,.2f}</b>" },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: "pointer",
        dataLabels: { enabled: false },
        borderWidth: 1
      }
    },
    series: [{ type: "pie", data: points, size: "100%", center: ["50%", "52%"] }],
    colors: COLORS,
    credits: { enabled: false },
    legend: { enabled: false },
    exporting: { enabled: false },
    navigation: { buttonOptions: { enabled: false } }
  };

  const emptyPieOptions: Highcharts.Options = {
    ...pieOptions,
    tooltip: { enabled: false },
    series: [
      {
        type: "pie",
        data: [{ name: "Empty", y: 100, color: "#F5F6F7" }],
        size: "100%",
        center: ["50%", "52%"],
        enableMouseTracking: false
      }
    ]
  };

  return (
    <Box
      sx={{
        height: 205,
        maxWidth: "50%",
        flexGrow: 1,
        borderRadius: "16px",
        backgroundColor: "#ffffff",
        boxShadow: "0px 1px 2px 0px #12121714, 0px 1px 4px 0px #1212171F",
        overflow: "hidden"
      }}
    >
      <AsyncContentMui isLoading={loading}>
        <Box sx={{ display: "flex", alignItems: "stretch", height: 205 }}>
          <Box sx={{ width: "48%", flexShrink: 0 }}>
            <HighchartsReact highcharts={Highcharts} options={hasData ? pieOptions : emptyPieOptions} />
          </Box>

          {hasData ? (
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                gap: "10px",
                pr: "16px"
              }}
            >
              {points.map((point, i) => (
                <Box key={`${point.name}-${i}`} sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      backgroundColor: COLORS[i % COLORS.length],
                      flexShrink: 0
                    }}
                  />
                  <Typography
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      fontSize: "16px",
                      fontWeight: 400,
                      fontFamily: "Roboto",
                      color: "#071F35",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap"
                    }}
                  >
                    {point.name}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "16px",
                      fontWeight: 400,
                      fontFamily: "Roboto",
                      color: "#071F35",
                      flexShrink: 0
                    }}
                  >
                    {total > 0 ? (((point.y ?? 0) / total) * 100).toFixed(2) : "0.00"}%
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                color: "#92A7BC",
                fontSize: "16px",
                fontFamily: "Roboto",
                px: "16px"
              }}
            >
              {data?.title === "Target"
                ? "Add your target allocation above — the graph will appear here once you're done."
                : "There’s no actual data yet. Add your current holdings to see how your portfolio is really allocated."}
            </Box>
          )}
        </Box>
      </AsyncContentMui>
    </Box>
  );
};
